import React, { useState, useEffect } from 'react';
import { knownVulnerabilities } from '../data/medical-device-database';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, Legend } from 'recharts';
import Analytics from '../utils/analytics';

export default function ThreatAssessment({ threats, setThreats, selectedDevice }) {
  const [activeVulnerabilityCategory, setActiveVulnerabilityCategory] = useState(null);
  const [selectedVulnerability, setSelectedVulnerability] = useState(null);
  const [showVulnerabilityLibrary, setShowVulnerabilityLibrary] = useState(false);
  
  // Update threats based on selected device
  useEffect(() => {
    if (selectedDevice && selectedDevice.threatProfiles) {
      const updatedThreats = threats.map(threat => {
        const threatKey = threat.name.toLowerCase().replace(/\s+/g, '');
        const profileValue = selectedDevice.threatProfiles[threatKey] || 1;
        
        return {
          ...threat,
          present: profileValue > 1,
          severity: profileValue,
          // Keep current likelihood if available, otherwise default to medium
          likelihood: threat.likelihood || 3,
          score: profileValue * (threat.likelihood || 3)
        };
      });
      
      setThreats(updatedThreats);
    }
  }, [selectedDevice, setThreats]);
  
  // Handle changes to threat properties
  const handleThreatChange = (index, field, value) => {
    const updatedThreats = [...threats];
    const threatName = updatedThreats[index].name;
    updatedThreats[index][field] = field === 'present' ? value : Number(value);
    
    // Track the threat change
    Analytics.trackEvent('Threat Assessment', 
      `${field.charAt(0).toUpperCase() + field.slice(1)} Change`, 
      `${threatName}: ${value}`
    );
    
    if (field === 'severity' || field === 'likelihood') {
      updatedThreats[index].score = updatedThreats[index].severity * updatedThreats[index].likelihood;
    }
    setThreats(updatedThreats);
  };
  
  // Find matching vulnerabilities from the database for a threat
  const findMatchingVulnerabilities = (threatName) => {
    const threat = threatName.toLowerCase();
    const matches = [];
    
    knownVulnerabilities.categories.forEach(category => {
      category.vulnerabilities.forEach(vuln => {
        if (
          (threat.includes('spoofing') && (vuln.name.toLowerCase().includes('authentication') || vuln.name.toLowerCase().includes('credential'))) ||
          (threat.includes('tampering') && (vuln.name.toLowerCase().includes('tamper') || vuln.name.toLowerCase().includes('integrity'))) ||
          (threat.includes('repudiation') && (vuln.name.toLowerCase().includes('audit') || vuln.name.toLowerCase().includes('logging'))) ||
          (threat.includes('information') && (vuln.name.toLowerCase().includes('disclosure') || vuln.name.toLowerCase().includes('encryption'))) ||
          (threat.includes('denial') && (vuln.name.toLowerCase().includes('denial') || vuln.name.toLowerCase().includes('availability'))) ||
          (threat.includes('elevation') && (vuln.name.toLowerCase().includes('privilege') || vuln.name.toLowerCase().includes('access')))
        ) {
          matches.push({
            category: category.name,
            vulnerability: vuln
          });
        }
      });
    });
    
    return matches;
  };
  
  // Get severity display elements
  const getSeverityElements = (level) => {
    const elements = [];
    for (let i = 1; i <= 5; i++) {
      elements.push(
        <div 
          key={i} 
          className={`w-5 h-5 rounded-full ${i <= level ? 'bg-red-500' : 'bg-gray-200'}`}
          title={`Severity level ${i}`}
        />
      );
    }
    return elements;
  };
  
  // Get likelihood display elements
  const getLikelihoodElements = (level) => {
    const elements = [];
    for (let i = 1; i <= 5; i++) {
      elements.push(
        <div 
          key={i} 
          className={`w-5 h-5 rounded-full ${i <= level ? 'bg-blue-500' : 'bg-gray-200'}`}
          title={`Likelihood level ${i}`}
        />
      );
    }
    return elements;
  };
  
  // Apply a vulnerability to a threat
  const applyVulnerabilityToThreat = (threatIndex, vulnerability) => {
    const newThreats = [...threats];
    const threat = newThreats[threatIndex];
    
    // Convert severity from text to number if needed
    let severityValue = 3; // Default medium
    if (vulnerability.severity === 'Critical') severityValue = 5;
    else if (vulnerability.severity === 'High') severityValue = 4;
    else if (vulnerability.severity === 'Medium') severityValue = 3;
    else if (vulnerability.severity === 'Low') severityValue = 2;
    
    threat.present = true;
    threat.severity = severityValue;
    threat.score = severityValue * threat.likelihood;
    threat.vulnerabilityReference = `${vulnerability.id}: ${vulnerability.name}`;
    
    setThreats(newThreats);
    setSelectedVulnerability(null);
    setShowVulnerabilityLibrary(false);
  };
  
  // Radar chart data
  const radarData = threats
    .filter(threat => threat.present)
    .map(threat => ({
      subject: threat.name,
      score: threat.score,
      fullMark: 25,
    }));
    
  // Severity and likelihood descriptions
  const severityDescriptions = [
    "1 - Minimal impact: No significant effect on safety, performance, or data security",
    "2 - Low impact: Minor effects on operations, limited potential for harm",
    "3 - Moderate impact: Noticeable performance degradation or potential for data exposure",
    "4 - High impact: Significant device impairment or substantial data compromise risk",
    "5 - Critical impact: Complete device failure or severe patient safety/data security risk"
  ];
  
  const likelihoodDescriptions = [
    "1 - Very unlikely: Almost never occurs in similar environments",
    "2 - Unlikely: Occurs rarely in similar environments",
    "3 - Possible: Sometimes occurs in similar environments",
    "4 - Likely: Regularly occurs in similar environments",
    "5 - Almost certain: Almost always occurs in similar environments"
  ];

  return (
    <div className="bg-white p-6 rounded shadow mb-8">
      <h2 className="text-xl font-semibold mb-4">STRIDE Threat Assessment</h2>
      
      <div className="mb-4 bg-blue-50 p-3 rounded">
        <p className="text-sm">
          The STRIDE methodology identifies six common threat categories that could affect your medical device:
          <strong>S</strong>poofing, <strong>T</strong>ampering, <strong>R</strong>epudiation, <strong>I</strong>nformation Disclosure, 
          <strong>D</strong>enial of Service, and <strong>E</strong>levation of Privilege.
        </p>
        <p className="text-sm mt-2">
          For each applicable threat, indicate whether it's present, then rate its severity and likelihood.
        </p>
      </div>
      
      {selectedDevice && (
        <div className="mb-4 p-3 bg-green-50 rounded">
          <p className="text-sm">
            <span className="font-medium">Using threat profile for:</span> {selectedDevice.name}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Default threat values have been pre-populated based on this device's typical risk profile.
            You can adjust these values as needed for your specific implementation.
          </p>
        </div>
      )}
      
      <div className="mb-6 flex justify-between items-center">
        <h3 className="font-medium">Threat Categories</h3>
        <button 
          className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          onClick={() => setShowVulnerabilityLibrary(!showVulnerabilityLibrary)}
        >
          {showVulnerabilityLibrary ? 'Hide Vulnerability Library' : 'Show Vulnerability Library'}
        </button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="overflow-x-auto">
            <table className="w-full mb-4">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-2 border-b">Threat</th>
                  <th className="text-center p-2 border-b">Present?</th>
                  <th className="text-center p-2 border-b">Severity (1-5)</th>
                  <th className="text-center p-2 border-b">Likelihood (1-5)</th>
                  <th className="text-center p-2 border-b">Score</th>
                </tr>
              </thead>
              <tbody>
                {threats.map((threat, index) => (
                  <tr key={index} className={`border-b ${threat.present ? 'bg-yellow-50' : ''}`}>
                    <td className="p-2">
                      <div className="font-medium">{threat.name}</div>
                      <div className="text-xs text-gray-500">{threat.description}</div>
                      {threat.vulnerabilityReference && (
                        <div className="text-xs mt-1 text-blue-600">Ref: {threat.vulnerabilityReference}</div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <input 
                        type="checkbox" 
                        checked={threat.present} 
                        onChange={(e) => handleThreatChange(index, 'present', e.target.checked)}
                      />
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col items-center space-y-2">
                        <select 
                          className={`w-full p-1 border rounded ${!threat.present && 'opacity-50'}`}
                          value={threat.severity}
                          onChange={(e) => handleThreatChange(index, 'severity', e.target.value)}
                          disabled={!threat.present}
                          title={severityDescriptions[threat.severity - 1]}
                        >
                          <option value="1">1 - Minimal</option>
                          <option value="2">2 - Low</option>
                          <option value="3">3 - Moderate</option>
                          <option value="4">4 - High</option>
                          <option value="5">5 - Critical</option>
                        </select>
                        <div className="flex space-x-1 w-full justify-center">
                          {getSeverityElements(threat.severity)}
                        </div>
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col items-center space-y-2">
                        <select 
                          className={`w-full p-1 border rounded ${!threat.present && 'opacity-50'}`}
                          value={threat.likelihood}
                          onChange={(e) => handleThreatChange(index, 'likelihood', e.target.value)}
                          disabled={!threat.present}
                          title={likelihoodDescriptions[threat.likelihood - 1]}
                        >
                          <option value="1">1 - Very Unlikely</option>
                          <option value="2">2 - Unlikely</option>
                          <option value="3">3 - Possible</option>
                          <option value="4">4 - Likely</option>
                          <option value="5">5 - Almost Certain</option>
                        </select>
                        <div className="flex space-x-1 w-full justify-center">
                          {getLikelihoodElements(threat.likelihood)}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-center font-bold">
                      {threat.present ? (
                        <span className={`px-2 py-1 rounded ${
                          threat.score >= 16 ? 'bg-red-100 text-red-800' : 
                          threat.score >= 9 ? 'bg-orange-100 text-orange-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {threat.score}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Legend and Help */}
          <div className="bg-gray-50 p-3 rounded text-sm">
            <h4 className="font-medium mb-2">Scoring Guide</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium text-xs mb-1">Severity</p>
                <ul className="list-disc ml-4 text-xs space-y-1">
                  {severityDescriptions.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-medium text-xs mb-1">Likelihood</p>
                <ul className="list-disc ml-4 text-xs space-y-1">
                  {likelihoodDescriptions.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          {/* Radar Chart */}
          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2">Threat Visualization</h3>
            <div className="border rounded h-64 p-4 bg-gray-50">
              {radarData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 25]} />
                    <Radar 
                      name="Threat Score" 
                      dataKey="score" 
                      stroke="#FF8042" 
                      fill="#FF8042" 
                      fillOpacity={0.6} 
                    />
                    <Tooltip />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  No threats marked as present. Select applicable threats to visualize risk profile.
                </div>
              )}
            </div>
          </div>
          
          {/* Known Vulnerabilities Library */}
          {showVulnerabilityLibrary && (
            <div className="border rounded p-4">
              <h3 className="font-medium mb-2">Known Vulnerabilities Library</h3>
              <p className="text-sm text-gray-600 mb-4">
                Browse known vulnerabilities that may apply to this device. Click a category to explore.
              </p>
              
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {knownVulnerabilities.categories.map(category => (
                    <button
                      key={category.id}
                      className={`text-xs px-3 py-1 rounded ${
                        activeVulnerabilityCategory === category.id 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                      onClick={() => setActiveVulnerabilityCategory(
                        activeVulnerabilityCategory === category.id ? null : category.id
                      )}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {activeVulnerabilityCategory && (
                <div className="bg-gray-50 p-3 rounded max-h-80 overflow-y-auto">
                  <h4 className="font-medium mb-2">
                    {knownVulnerabilities.categories.find(c => c.id === activeVulnerabilityCategory)?.name}
                  </h4>
                  <p className="text-xs text-gray-600 mb-2">
                    {knownVulnerabilities.categories.find(c => c.id === activeVulnerabilityCategory)?.description}
                  </p>
                  
                  {knownVulnerabilities.categories
                    .find(c => c.id === activeVulnerabilityCategory)
                    ?.vulnerabilities.map(vuln => (
                      <div key={vuln.id} className="mb-3 border rounded p-2 bg-white">
                        <div className="flex justify-between items-start">
                          <h5 className="font-medium text-sm">{vuln.name}</h5>
                          <span className={`text-xs px-2 py-1 rounded ${
                            vuln.severity === 'Critical' ? 'bg-red-100 text-red-800' : 
                            vuln.severity === 'High' ? 'bg-orange-100 text-orange-800' : 
                            vuln.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-green-100 text-green-800'
                          }`}>
                            {vuln.severity}
                          </span>
                        </div>
                        <p className="text-xs my-2">{vuln.description}</p>
                        <div className="text-xs text-gray-600 mb-1">
                          Affected device types: {vuln.affectedDeviceTypes.join(', ')}
                        </div>
                        <div className="text-xs mb-1">
                          Mitigation: {vuln.mitigation}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {threats.map((threat, i) => {
                            // Match vulnerabilities to STRIDE categories
                            let matches = false;
                            const tlc = threat.name.toLowerCase();
                            const vlc = vuln.name.toLowerCase();
                            const desc = vuln.description.toLowerCase();
                            
                            if (
                              (tlc.includes('spoofing') && (vlc.includes('authentication') || vlc.includes('credential') || desc.includes('identity'))) ||
                              (tlc.includes('tampering') && (vlc.includes('tamper') || vlc.includes('integrity') || vlc.includes('modif'))) ||
                              (tlc.includes('repudiation') && (vlc.includes('audit') || vlc.includes('logging') || desc.includes('track'))) ||
                              (tlc.includes('information') && (vlc.includes('disclosure') || vlc.includes('encryption') || vlc.includes('leak'))) ||
                              (tlc.includes('denial') && (vlc.includes('denial') || vlc.includes('availability') || desc.includes('disrupt'))) ||
                              (tlc.includes('elevation') && (vlc.includes('privilege') || vlc.includes('access') || desc.includes('unauthor')))
                            ) {
                              matches = true;
                            }
                            
                            return matches ? (
                              <button
                                key={i}
                                className="text-xs px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded"
                                onClick={() => applyVulnerabilityToThreat(i, vuln)}
                                title={`Apply to ${threat.name}`}
                              >
                                Apply to {threat.name}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Recent Threats Section */}
      <div className="mt-6 bg-orange-50 p-4 rounded">
        <h3 className="font-medium mb-2">Recent Threat Trends</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {knownVulnerabilities.recentThreats.map(threat => (
            <div key={threat.id} className="bg-white p-3 rounded border text-sm">
              <h4 className="font-medium">{threat.name}</h4>
              <p className="text-xs my-2">{threat.description}</p>
              <p className="text-xs text-orange-700">{threat.mitigationStrategy}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}